import { Store } from '../Store/Store';
import propertyService from './PropertyService';

class PropertyCacheService {
  constructor() {
    this.cacheInvalidationInterval = null;
    this.lastInvalidationCheck = Date.now();
    this.invalidationCheckInterval = 2 * 60 * 1000; // Check every 2 minutes
  }

  /**
   * Start automatic cache invalidation monitoring
   */
  startCacheMonitoring() {
    if (this.cacheInvalidationInterval) {
      return; // Already monitoring
    }

    console.log('PropertyCacheService - Starting cache monitoring...');
    
    this.cacheInvalidationInterval = setInterval(() => {
      this.checkAndInvalidateCache();
    }, this.invalidationCheckInterval);
  }

  /**
   * Stop automatic cache invalidation monitoring
   */
  stopCacheMonitoring() {
    if (this.cacheInvalidationInterval) {
      clearInterval(this.cacheInvalidationInterval);
      this.cacheInvalidationInterval = null;
      console.log('PropertyCacheService - Stopped cache monitoring');
    }
  }

  /**
   * Check if cache should be invalidated and do so if needed
   * DISABLED: No automatic cache invalidation/refresh - user must manually refresh
   */
  async checkAndInvalidateCache() {
    // Auto-refresh disabled - cache will remain valid until manually refreshed
    // This prevents automatic page refreshes every 5-10 minutes
    return;
    
    // Original code (disabled):
    // try {
    //   const state = Store.getState();
    //   const { lastFetchTime, cacheExpiry } = state.rentalProperties;
    //   
    //   if (!lastFetchTime) {
    //     return; // No cache to invalidate
    //   }
    //
    //   const now = Date.now();
    //   const timeSinceLastFetch = now - lastFetchTime;
    //   
    //   // If cache is expired, invalidate it
    //   if (timeSinceLastFetch > cacheExpiry) {
    //     console.log('PropertyCacheService - Cache expired, invalidating...');
    //     await this.invalidateCache();
    //   }
    // } catch (error) {
    //   console.error('PropertyCacheService - Error checking cache:', error);
    // }
  }

  /**
   * Manually invalidate cache
   */
  async invalidateCache() {
    try {
      console.log('PropertyCacheService - Invalidating cache...');
      
      // Clear service cache
      propertyService.clearCache();
      
      // Dispatch invalidation action
      Store.dispatch({
        type: 'rentalProperties/invalidateCache'
      });
      
      console.log('PropertyCacheService - Cache invalidated successfully');
    } catch (error) {
      console.error('PropertyCacheService - Error invalidating cache:', error);
    }
  }

  /**
   * Refresh properties and update cache
   */
  async refreshProperties() {
    try {
      console.log('PropertyCacheService - Refreshing properties...');
      
      // Dispatch refresh action
      Store.dispatch({
        type: 'rentalProperties/refreshProperties'
      });
      
      // Load fresh properties
      await propertyService.refreshProperties(Store.dispatch);
      
      console.log('PropertyCacheService - Properties refreshed successfully');
    } catch (error) {
      console.error('PropertyCacheService - Error refreshing properties:', error);
    }
  }

  /**
   * Get cache status information
   */
  getCacheStatus() {
    const state = Store.getState();
    const { lastFetchTime, cacheExpiry, properties } = state.rentalProperties;
    
    if (!lastFetchTime) {
      return {
        isValid: false,
        isExpired: true,
        timeUntilExpiry: 0,
        propertyCount: 0,
        lastFetchTime: null,
      };
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    const timeUntilExpiry = Math.max(0, cacheExpiry - timeSinceLastFetch);
    const isExpired = timeSinceLastFetch > cacheExpiry;

    return {
      isValid: !isExpired,
      isExpired,
      timeUntilExpiry,
      propertyCount: properties.length,
      lastFetchTime: new Date(lastFetchTime),
      cacheExpiry: cacheExpiry,
    };
  }

  /**
   * Force cache refresh if needed
   */
  async ensureFreshCache() {
    const cacheStatus = this.getCacheStatus();
    
    if (cacheStatus.isExpired || cacheStatus.propertyCount === 0) {
      console.log('PropertyCacheService - Cache is stale or empty, refreshing...');
      await this.refreshProperties();
    } else {
      console.log('PropertyCacheService - Cache is fresh, no refresh needed');
    }
  }

  /**
   * Pre-warm cache for better performance
   */
  async preWarmCache() {
    try {
      console.log('PropertyCacheService - Pre-warming cache...');
      
      const state = Store.getState();
      const { properties } = state.rentalProperties;
      
      // If we don't have properties or cache is expired, pre-load
      if (properties.length === 0) {
        await propertyService.preloadProperties(Store.dispatch);
      }
      
      console.log('PropertyCacheService - Cache pre-warmed successfully');
    } catch (error) {
      console.error('PropertyCacheService - Error pre-warming cache:', error);
    }
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics() {
    return {
      ...propertyService.getPerformanceMetrics(),
      cacheStatus: this.getCacheStatus(),
      isMonitoring: !!this.cacheInvalidationInterval,
    };
  }

  /**
   * Cleanup method
   */
  cleanup() {
    this.stopCacheMonitoring();
  }
}

// Create singleton instance
const propertyCacheService = new PropertyCacheService();

export default propertyCacheService;
